/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import styled from "styled-components";
import Container from "../../components/Container";
import NavigationHeader from "../../components/NavigationHeader";
import Stack from "../../components/ui/Stack";
import Paginator from "../../components/ui/Paginator";
import Dimmed from "../../components/ui/Dimmed";
import PluginCardSkeleton from "../../components/skeleton/PluginCardSkeleton";
import EmptyView from "../../components/EmptyView";
import Icon from "../../components/ui/Icon";
import ClientMembershipItem from "../../components/membership/ClientMembershipItem";
import { useGetPurchasedMemberships } from "../../lib/react-query/useGetPurchasedMemberships";
import { useAuth } from "../../hooks/useAuth";
import { ROUTES } from "../../constants/constants";
import Button from "../../components/ui/Button";
import { useNavigate } from "react-router-dom";

const Grid = styled(Stack)`
  grid-template-columns: repeat(auto-fill, minmax(min(314px, 100%), 1fr));
`;

const ClientMemberships = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const { data, isLoading, isFetching, isPlaceholderData } = useGetPurchasedMemberships(
    { page: currentPage },
    { enabled: isAuthenticated, placeholderData: keepPreviousData }
  );

  const memberships = data?.data || [];
  const totalPages = data?.metadata?.pages_count || 1;

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <Container>
      <NavigationHeader title="My memberships" cart clientLogin back={ROUTES.HOME} />
      <Stack $gap="1rem">
        {isLoading && (
          <Grid $gap="0.5rem">
            <PluginCardSkeleton withIncludes />
          </Grid>
        )}

        {!isLoading && memberships.length === 0 && (
          <EmptyView icon={Icon.Membership} description="You don't have any memberships yet" action={<Button title="Explore memberships" onClick={() => navigate(ROUTES.MEMBERSHIPS)} />} />
        )}

        {!isLoading && memberships.length > 0 && (
          <>
            <Dimmed $active={isPlaceholderData}>
              <Grid $gap="0.5rem">
                {memberships.map((purchase) => (
                  <ClientMembershipItem key={purchase.id} purchase={purchase} />
                ))}
              </Grid>
            </Dimmed>
            {totalPages > 1 && (
              <Paginator
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                isLoading={isFetching}
              />
            )}
          </>
        )}
      </Stack>
    </Container>
  );
};

export default ClientMemberships;
